import { FieldGrid } from '../physics/FieldGrid';

export type FieldViewMode = 'intensity' | 'amplitude';

/**
 * Renders FDTD field grid as a color-mapped overlay on the canvas.
 * Supports two view modes:
 * - intensity: hot colormap (black → blue → cyan → white) of E²
 * - amplitude: diverging colormap (blue → white → red) showing E-field sign/phase
 */
export class FieldRenderer {
  private ctx: CanvasRenderingContext2D;
  private imageDataCache: Map<number, ImageData> = new Map();
  private tmpCanvasCache: Map<number, HTMLCanvasElement> = new Map();
  viewMode: FieldViewMode = 'intensity';

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  drawFieldGrids(grids: FieldGrid[]): void {
    for (const grid of grids) {
      this.drawGrid(grid);
    }
  }

  private drawGrid(grid: FieldGrid): void {
    if (this.viewMode === 'amplitude') {
      this.drawAmplitudeGrid(grid);
    } else {
      this.drawIntensityGrid(grid);
    }
  }

  private drawIntensityGrid(grid: FieldGrid): void {
    const res = grid.resolution;
    const b = grid.bounds;
    const imgData = this.getImageData(res);
    const data = imgData.data;
    const intensity = grid.intensity;

    let maxIntensity = 0;
    for (let i = 0; i < intensity.length; i++) {
      if (intensity[i] > maxIntensity) maxIntensity = intensity[i];
    }
    if (maxIntensity < 1e-10) return;

    const invMax = 1 / maxIntensity;

    for (let y = 0; y < res; y++) {
      for (let x = 0; x < res; x++) {
        const idx = y * res + x;
        const pixIdx = idx * 4;
        const val = Math.sqrt(intensity[idx] * invMax);

        if (val < 0.001) {
          data[pixIdx] = 0;
          data[pixIdx + 1] = 0;
          data[pixIdx + 2] = 0;
          data[pixIdx + 3] = 0;
        } else {
          data[pixIdx] = Math.min(255, Math.round(val * val * 255));
          data[pixIdx + 1] = Math.min(255, Math.round(val * 180));
          data[pixIdx + 2] = Math.min(255, Math.round(Math.sqrt(val) * 255));
          data[pixIdx + 3] = Math.min(200, Math.round(val * 200));
        }
      }
    }

    this.blitToCanvas(imgData, res, b);
  }

  /**
   * Amplitude view: shows E-field with sign using a diverging colormap.
   * Blue = negative, white = zero, red = positive.
   * Reveals interference fringes and phase relationships.
   */
  private drawAmplitudeGrid(grid: FieldGrid): void {
    const res = grid.resolution;
    const b = grid.bounds;
    const imgData = this.getImageData(res);
    const data = imgData.data;
    const wc = grid.wavelengthCount;

    // Sum E_x over all wavelength channels for each spatial cell to get amplitude
    let maxAmp = 0;
    const amplitudes = new Float32Array(res * res);
    for (let y = 0; y < res; y++) {
      for (let x = 0; x < res; x++) {
        let sum = 0;
        const base = (y * res + x) * wc;
        for (let w = 0; w < wc; w++) {
          // Use E_x as the primary field component for visualization
          sum += grid.E_x[base + w];
        }
        amplitudes[y * res + x] = sum;
        const abs = Math.abs(sum);
        if (abs > maxAmp) maxAmp = abs;
      }
    }

    if (maxAmp < 1e-10) return;

    const invMax = 1 / maxAmp;

    // Diverging colormap: blue (-1) → white (0) → red (+1)
    for (let y = 0; y < res; y++) {
      for (let x = 0; x < res; x++) {
        const idx = y * res + x;
        const pixIdx = idx * 4;
        const val = amplitudes[idx] * invMax; // -1 to +1

        const absVal = Math.abs(val);
        if (absVal < 0.005) {
          data[pixIdx] = 0;
          data[pixIdx + 1] = 0;
          data[pixIdx + 2] = 0;
          data[pixIdx + 3] = 0;
          continue;
        }

        const t = Math.sqrt(absVal); // perceptual scaling
        if (val > 0) {
          // White → Red
          data[pixIdx] = Math.round(255 * t);
          data[pixIdx + 1] = Math.round(255 * (1 - t) * t);
          data[pixIdx + 2] = Math.round(255 * (1 - t) * t * 0.5);
        } else {
          // White → Blue
          data[pixIdx] = Math.round(255 * (1 - t) * t * 0.5);
          data[pixIdx + 1] = Math.round(255 * (1 - t) * t);
          data[pixIdx + 2] = Math.round(255 * t);
        }
        data[pixIdx + 3] = Math.min(220, Math.round(t * 220));
      }
    }

    this.blitToCanvas(imgData, res, b);
  }

  private getImageData(res: number): ImageData {
    let imgData = this.imageDataCache.get(res);
    if (!imgData || imgData.width !== res) {
      if (this.imageDataCache.size >= 4) {
        const oldest = this.imageDataCache.keys().next().value!;
        this.imageDataCache.delete(oldest);
      }
      imgData = new ImageData(res, res);
      this.imageDataCache.set(res, imgData);
    }
    return imgData;
  }

  private blitToCanvas(imgData: ImageData, res: number, b: { x: number; y: number; width: number; height: number }): void {
    // Reuse temporary canvas per resolution
    let tmpCanvas = this.tmpCanvasCache.get(res);
    if (!tmpCanvas || tmpCanvas.width !== res) {
      tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = res;
      tmpCanvas.height = res;
      this.tmpCanvasCache.set(res, tmpCanvas);
    }
    const tmpCtx = tmpCanvas.getContext('2d')!;
    tmpCtx.putImageData(imgData, 0, 0);

    this.ctx.save();
    this.ctx.globalCompositeOperation = 'screen';
    this.ctx.drawImage(tmpCanvas, b.x, b.y, b.width, b.height);
    this.ctx.restore();
  }

  /** Draw debug outline for active field grid regions */
  drawGridBounds(grids: FieldGrid[]): void {
    this.ctx.save();
    for (const grid of grids) {
      const b = grid.bounds;
      this.ctx.strokeStyle = 'rgba(255, 100, 50, 0.3)';
      this.ctx.lineWidth = 1;
      this.ctx.setLineDash([4, 4]);
      this.ctx.strokeRect(b.x, b.y, b.width, b.height);

      const memKB = (grid.memoryBytes / 1024).toFixed(0);
      this.ctx.font = '9px monospace';
      this.ctx.fillStyle = 'rgba(255, 100, 50, 0.5)';
      this.ctx.fillText(`${memKB}KB`, b.x + 2, b.y + 10);
    }
    this.ctx.restore();
  }
}
